import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar as CalendarIcon, Clock, MapPin, Users, DollarSign, CheckCircle, AlertCircle, Filter, Search, ChevronLeft, ChevronRight, Plus, ArrowLeft } from 'lucide-react'
import { toast } from '../lib/toast'

interface ScheduleJob {
  id: string
  clientName: string
  service: string
  date: string
  time: string
  duration: number
  location: string
  amount: number
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled'
  notes?: string
  recurring?: boolean
}

export default function ProviderSchedule() {
  const navigate = useNavigate()
  
  const [jobs] = React.useState<ScheduleJob[]>([
    {
      id: '1',
      clientName: 'Sarah Johnson',
      service: 'Standard House Cleaning',
      date: '2024-03-19',
      time: '09:00 AM',
      duration: 3,
      location: '123 Main St, Downtown Apartment',
      amount: 150,
      status: 'scheduled',
      notes: 'Focus on kitchen and bathrooms',
      recurring: true
    },
    {
      id: '2',
      clientName: 'Mike Williams',
      service: 'Deep Kitchen Cleaning',
      date: '2024-03-19',
      time: '02:00 PM',
      duration: 4,
      location: '456 Oak Ave, Suburban House',
      amount: 200,
      status: 'scheduled',
      notes: 'Bring extra cleaning supplies'
    },
    {
      id: '3',
      clientName: 'Emily Davis',
      service: 'Office Cleaning',
      date: '2024-03-18',
      time: '10:00 AM',
      duration: 5,
      location: '789 Business Center Blvd',
      amount: 300,
      status: 'in-progress',
      notes: 'After-hours access code: 1234'
    },
    {
      id: '4',
      clientName: 'Robert Brown',
      service: 'Carpet Shampooing',
      date: '2024-03-17',
      time: '11:00 AM',
      duration: 2,
      location: '321 Residential Complex',
      amount: 120,
      status: 'completed',
      notes: 'Client very satisfied'
    },
    {
      id: '5',
      clientName: 'Lisa Anderson',
      service: 'Standard House Cleaning',
      date: '2024-03-20',
      time: '01:00 PM',
      duration: 3,
      location: '555 Elm Street',
      amount: 150,
      status: 'scheduled',
      recurring: true
    }
  ])

  const handleBackToDashboard = () => {
    navigate('/dashboard')
  }

  const [searchTerm, setSearchTerm] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<string>('all')
  const [viewMode, setViewMode] = React.useState<'list' | 'calendar'>('list')

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />
      case 'in-progress':
        return <Clock className="w-4 h-4" />
      case 'scheduled':
        return <CalendarIcon className="w-4 h-4" />
      case 'cancelled':
        return <AlertCircle className="w-4 h-4" />
      default:
        return <CalendarIcon className="w-4 h-4" />
    }
  }

  // Calendar view logic
  const [currentMonth, setCurrentMonth] = React.useState(new Date())
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null)

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()
    
    const days = []
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i))
    }
    return days
  }

  const getJobsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0]
    return filteredJobs.filter(job => job.date === dateStr)
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev)
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1)
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1)
      }
      return newMonth
    })
  }

  const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const calendarDays = getDaysInMonth(currentMonth)

  const handleStartJob = (jobId: string) => {
    toast.success('Job started! Timer is now running.')
    // In real app, this would start a timer and update job status
  }

  const handleCompleteJob = (jobId: string) => {
    toast.success('Job marked as completed!')
    // In real app, this would prompt for rating and update job status
  }

  const handleReschedule = (jobId: string) => {
    toast.info('Reschedule feature coming soon!')
  }

  const groupedJobs = filteredJobs.reduce((acc, job) => {
    const date = job.date
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(job)
    return acc
  }, {} as Record<string, ScheduleJob[]>)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <CalendarIcon className="w-6 h-6 text-green-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">My Schedule</h1>
                <p className="text-sm text-gray-500 dark:text-dark-text-secondary">Manage your cleaning appointments</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by client, service, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                List View
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  viewMode === 'calendar' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Calendar View
              </button>
            </div>
          </div>
        </div>

        {/* Schedule Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 md:gap-4 mb-6">
          <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Today</p>
                <p className="text-xl font-bold text-blue-600">2 Jobs</p>
              </div>
              <CalendarIcon className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">This Week</p>
                <p className="text-xl font-bold text-green-600">5 Jobs</p>
              </div>
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Total Hours</p>
                <p className="text-xl font-bold text-purple-600">17 hrs</p>
              </div>
              <Clock className="w-6 h-6 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Earnings</p>
                <p className="text-xl font-bold text-yellow-600">$920</p>
              </div>
              <DollarSign className="w-6 h-6 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Jobs List */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
              Scheduled Jobs ({filteredJobs.length})
            </h2>
          </div>
          
          <div className="px-6 py-6">
            {viewMode === 'list' ? (
              <div className="space-y-6">
                {/* Show all jobs in a single 3-column grid */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-1 md:gap-3">
                  {filteredJobs.map((job) => (
                    <div key={job.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 md:p-3 hover:shadow-md transition-shadow min-h-[200px] md:min-h-[280px]">
                      <div className="flex flex-col h-full">
                        {/* Header with client name and status */}
                        <div className="mb-2">
                          <div className="flex items-start justify-between mb-1">
                            <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary text-sm leading-tight flex-1 pr-1">{job.clientName}</h3>
                            <div className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(job.status)} flex-shrink-0`}>
                              {getStatusIcon(job.status)}
                              <span className="hidden xs:inline">{job.status.charAt(0).toUpperCase() + job.status.slice(1).replace('-', ' ')}</span>
                              <span className="xs:hidden">{job.status.charAt(0).toUpperCase()}</span>
                            </div>
                          </div>
                          {job.recurring && (
                            <span className="inline-block px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs rounded-full">
                              Recurring
                            </span>
                          )}
                        </div>
                        
                        {/* Service info */}
                        <div className="flex-1 space-y-1.5 mb-2">
                          <p className="text-xs text-gray-700 dark:text-dark-text-secondary line-clamp-1">
                            <span className="font-medium">Service:</span> {job.service}
                          </p>
                          
                          <div className="space-y-1 text-xs text-gray-600 dark:text-dark-text-secondary">
                            <div className="flex items-center space-x-1">
                              <CalendarIcon className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{job.date} • {job.time}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{job.duration}h</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate line-clamp-1">{job.location}</span>
                            </div>
                          </div>
                          
                          {job.notes && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 line-clamp-2">
                              <span className="font-medium">Notes:</span> {job.notes}
                            </p>
                          )}
                        </div>
                        
                        {/* Footer with price and actions */}
                        <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-auto">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-base font-bold text-green-600">${job.amount}</p>
                          </div>
                          <div className="space-y-1">
                            {job.status === 'scheduled' && (
                              <button
                                onClick={() => handleStartJob(job.id)}
                                className="w-full bg-blue-600 text-white px-1.5 py-1 rounded text-xs hover:bg-blue-700 transition-colors"
                              >
                                Start Job
                              </button>
                            )}
                            {job.status === 'in-progress' && (
                              <button
                                onClick={() => handleCompleteJob(job.id)}
                                className="w-full bg-green-600 text-white px-1.5 py-1 rounded text-xs hover:bg-green-700 transition-colors"
                              >
                                Complete
                              </button>
                            )}
                            <button
                              onClick={() => handleReschedule(job.id)}
                              className="w-full bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-1.5 py-1 rounded text-xs hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                            >
                              Reschedule
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Calendar Navigation */}
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">{monthYear}</h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigateMonth('prev')}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => navigateMonth('next')}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border p-4">
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-sm font-medium text-gray-600 dark:text-dark-text-secondary py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((day, index) => {
                      const dayJobs = day ? getJobsForDate(day) : []
                      const isToday = day && day.toDateString() === new Date().toDateString()
                      const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString()
                      
                      return (
                        <div
                          key={index}
                          onClick={() => day && setSelectedDate(day)}
                          className={`
                            min-h-[80px] p-2 border border-gray-200 dark:border-dark-border rounded-lg cursor-pointer transition-colors
                            ${!day ? 'bg-gray-50 dark:bg-gray-800 cursor-default' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}
                            ${isToday ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' : ''}
                            ${isSelected ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600' : ''}
                          `}
                        >
                          {day && (
                            <>
                              <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-900 dark:text-dark-text-primary'}`}>
                                {day.getDate()}
                              </div>
                              {dayJobs.length > 0 && (
                                <div className="mt-1 space-y-1">
                                  {dayJobs.slice(0, 2).map((job, jobIndex) => (
                                    <div
                                      key={jobIndex}
                                      className={`text-xs p-1 rounded truncate ${
                                        job.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                                        job.status === 'in-progress' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                                        job.status === 'scheduled' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                                        'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                      }`}
                                    >
                                      {job.clientName}
                                    </div>
                                  ))}
                                  {dayJobs.length > 2 && (
                                    <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                                      +{dayJobs.length - 2} more
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Selected Date Details */}
                {selectedDate && (
                  <div className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
                      {selectedDate.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </h3>
                    <div className="space-y-3">
                      {getJobsForDate(selectedDate).map(job => (
                        <div key={job.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-dark-text-primary">{job.clientName}</div>
                            <div className="text-sm text-gray-600 dark:text-dark-text-secondary">
                              {job.time} • {job.duration} hours • {job.service}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-dark-text-muted">{job.location}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-green-600">${job.amount}</div>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                              {getStatusIcon(job.status)}
                              <span className="ml-1">{job.status.charAt(0).toUpperCase() + job.status.slice(1)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {getJobsForDate(selectedDate).length === 0 && (
                        <p className="text-gray-500 dark:text-dark-text-muted text-center py-4">No jobs scheduled for this date</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
